import React, { useState, useEffect, useRef, useContext } from "react";
import InputMask from "react-input-mask";
import AppLabelsContext from "../../App/AppLabelsContext";
import { PasswordVisibilityToggle } from "./PasswordVisibilityToggle";
import "./FormInput.css";
import StoreContext from "../../OnlineOrdering/StoreContext";
import LocationsContext from "../../OnlineOrdering/Locations/LocationsContext";
import MerchantConfigContext from "../../App/MerchantConfigContext";
import AppLanguageContext from "../../App/AppLanguageContext";

// API helper functions
import { get1300Stores } from "../../OnlineOrdering/GoogleMaps/get1300Stores";
import AppSettingsContext from "../../App/AppSettingsContext";
import useWindowSize from "../hooks/useWindowSize";

// Phone number with country
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { PhoneNumberUtil } from "google-libphonenumber";

export const FormInput = (props) => {
  const {
    resetInvalidField,
    resetPatternTooltip,
    defaultValue,
    fieldData,
    isSmall,
    id,
    isMaskedInput,
    name,
    handleInputChangeFromParent,
    onInputChange,
    value,
    placeholder,
    ariaPlaceholder,
    type,
    title,
    maxlength,
    isRequired,
    autocomplete,
    label,
    showAstr,
    onInputBlur,
    onFocusAction,
    customLabelClass,
    customerWrapperClass,
    inputInformationText = null,
    maxValue,
    displayCurrencySymbol = false,
    isCountryCodeRequired = false,
    merchantCountry,
    setSelectedCountryCode,
  } = props;

  const [inputInFocusClass, setInputInFocusClass] = useState("");

  const activeOrderStore = useContext(StoreContext).activeOrderStore;
  let allLocations = useContext(LocationsContext);
  const merchantConfigContext = useContext(MerchantConfigContext);
  const appLanguage = useContext(AppLanguageContext);
  const { skin } = merchantConfigContext;
  const [displayCurrency, setDisplayCurrency] = useState(); // used to display currency symbol as prefix in the input field
  const deviceWidth = useWindowSize().width;
  const phoneUtil = PhoneNumberUtil.getInstance();

  const onFocus = (field) => {
    resetInvalidField(field);
    setInputInFocusClass(" form__field-wrapper--in-focus");
    if (onFocusAction) {
      onFocusAction();
    }
  };

  const onClickAway = (field) => {
    if (onInputBlur) {
      onInputBlur(field);
    } else {
      /* If any value is present keep placeholder minimized, otherwise expand it */
      field.value === "" && setInputInFocusClass("");
      resetPatternTooltip(field);
    }
  };

  // if Input has a default value - Set focused label class and perform its onSelectChange
  const inputRef = useRef();
  useEffect(() => {
    if (defaultValue && !inputInFocusClass && inputRef.current) {
      setInputInFocusClass(" form__field-wrapper--in-focus");
      onFocus(inputRef.current);
    }
  }, [inputRef.current]);

  const appLabels = useContext(AppLabelsContext);

  const [passwordType, setPasswordType] = useState("password");
  let [showWarningMessage, setShowWarningMessage] = useState(false);
  let [formattedPhoneNumber, setFormattedPhoneNumber] = useState("");
  let [countryCode, setCountryCode] = useState();
  let countryCodeFromContext = "";

  const [phoneNumberWithCountryCode, setPhoneNumberWithCountryCode] = useState("");
  const [phoneNumberCountry, setPhoneNumberCountry] = useState("");
  const [phoneNumberErrorMessage, setPhoneNumberErrorMessage] = useState(null);

  //converts a valid 10 digit phone number into (xxx) xxx-xxxx format
  let phoneNumberAutoFormat = (phoneNumber) => {
    const number = phoneNumber.trim().replace(/[^0-9]/g, "");

    if (number.length < 4) return number;
    if (number.length < 7) return number.replace(/(\d{3})(\d{1})/, "($1) $2");
    if (number.length < 11) return number.replace(/(\d{3})(\d{3})(\d{1})/, "($1) $2-$3");
    return number.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
  };

  //get the value of phone number and format it if user is in CA/US
  const handlePhoneNumberInput = (e) => {
    // only do formatting if US/CA country is selected
    if (countryCode === "US" || countryCode === "CA") {
      let value = e.target.value;
      value = value.replace(/([^+0-9]+)/gi, ""); //removes any character that is not number
      let formattedPhn = value;
      if (value.charAt(0) === "1") {
        setShowWarningMessage(true);
      }

      if (value.length <= 10)
        formattedPhn = phoneNumberAutoFormat(value); // retuns phone number in format (xxx) xxx-xxxx
      else if (value.length > 10) {
        // if input value is exceeding 10, set warning to true and return a substring of value upto 10 digits.
        value = value.substr(0, 10);
        formattedPhn = phoneNumberAutoFormat(value);
      }

      let length = formattedPhn.length;
      if (length <= 14 && value.charAt(0) !== "1") {
        setShowWarningMessage(false);
      }
      setFormattedPhoneNumber(formattedPhn);
    } else {
      let value = e.target.value;
      setFormattedPhoneNumber(value.replace(/([^+0-9]+)/gi, ""));
    }
  };

  //called when an input of type tel has a change in value.
  function onTelInputChange(e) {
    if (
      e.target.name === "phone" ||
      e.target.name === "phone-number" ||
      e.target.name === "cell-number"
    )
      handlePhoneNumberInput(e, phoneNumberCountry, phoneNumberWithCountryCode);
  }

  useEffect(() => {
    if (!allLocations && (name === "phone" || name === "phone-number" || name === "cell-number")) {
      get1300Stores(skin, "TAKE_OUT", appLanguage).then((apiStores) => {
        if (!apiStores || apiStores.length === 0) countryCodeFromContext = "";
        else allLocations = apiStores;
      });
    }

    if (activeOrderStore) {
      countryCodeFromContext = activeOrderStore.country;
      setCountryCode(activeOrderStore.country);
    } else if (allLocations && allLocations[0]) {
      countryCodeFromContext = allLocations[0].country;
      setCountryCode(allLocations[0].country);
    }
    // if there is an exisiting default value for input type tel ,format it for CA/US only
    if (
      (name === "phone" || name === "phone-number" || name === "cell-number") &&
      defaultValue &&
      defaultValue.length > 0 &&
      (countryCodeFromContext === "US" || countryCodeFromContext === "CA")
    ) {
      let formattedDefaultValue = phoneNumberAutoFormat(defaultValue);
      setFormattedPhoneNumber(formattedDefaultValue);
    } else if (
      (name === "phone" || name === "phone-number" || name === "cell-number") &&
      defaultValue
    ) {
      setFormattedPhoneNumber(defaultValue);
    }
  }, []);

  const appSettings = useContext(AppSettingsContext);
  let currencySymbol = appSettings["currency-symbol"];
  // set currency symbol form appSettings otherwise show $
  useEffect(() => {
    if (currencySymbol === "") {
      setDisplayCurrency("$");
    } else {
      setDisplayCurrency(currencySymbol);
    }
  }, [displayCurrencySymbol]);

  function checkForPositiveInteger(e) {
    if (e.key === "-") {
      e.preventDefault();
    }
  }

  const validatePhoneNumberWithCountryCode = () => {
    if (phoneNumberCountry && phoneNumberWithCountryCode) {
      const regionCode = phoneNumberCountry.countryCode.toUpperCase();
      const parsed = phoneUtil.parse(`${phoneNumberWithCountryCode}`, regionCode);
      if (!phoneUtil.isValidNumber(parsed)) {
        setPhoneNumberErrorMessage(appLabels["form"]["invalid-phone-number"]);
        return false;
      }
      setPhoneNumberErrorMessage("");
      return true;
    }
  };

  return (
    <>
      {!fieldData.isHidden && (
        <div
          style={!!props.isHidden ? { display: "none" } : {}}
          className={`form__field-wrapper${
            fieldData.isValid ? "" : " form__field-wrapper--invalid"
          }${isSmall ? " form__field-wrapper--small" : ""}
          ${customerWrapperClass ? customerWrapperClass : ""}
          ${inputInFocusClass} ${
            deviceWidth < 660 && name === "eGiftCardAmount" && inputInformationText
              ? " inputInfoTextMargin"
              : ""
          }`}
        >
          {inputInformationText && <span className="inputInfoText">{inputInformationText}</span>}
          {displayCurrencySymbol && <p className="selectIconStart">{displayCurrency}</p>}
          <label
            htmlFor={id}
            className={`${customLabelClass ? customLabelClass : "form__field-label"}`}
          >
            {!!showAstr && <span aria-hidden="true">*</span>}
            {(fieldData.label || label) +
              (fieldData.isRequired || isRequired
                ? ""
                : ` (${appLabels["form"]["optional-label"]})`)}
          </label>

          {isCountryCodeRequired ? (
            <PhoneInput
              country={merchantCountry.toLowerCase()}
              value={
                name === "phone" || name === "phone-number" || name === "cell-number"
                  ? formattedPhoneNumber
                  : value || fieldData.value
              }
              onChange={(phoneNumber, country, e, formattedValue) => {                
                const field = { name: name };
                onFocus(field);               
                setPhoneNumberErrorMessage("");
                setPhoneNumberWithCountryCode(phoneNumber);
                setPhoneNumberCountry(country);
                if (setSelectedCountryCode) {
                  setSelectedCountryCode(country.countryCode.toUpperCase());
                }
                if (handleInputChangeFromParent) handleInputChangeFromParent(e);
                onInputChange(e, false, null, isCountryCodeRequired, document.getElementById(id));
              }}
              inputStyle={{}}
              inputClass="form__field"
              onFocus={(e) => {
                onFocus(e.target);
              }}
              onBlur={(e) => {
                onClickAway(e.target);
                validatePhoneNumberWithCountryCode();
              }}
              onWheel={(e) => e.target.blur()}
              ismaskedinput="true"
              name={name}
              mask={fieldData.maskPattern}
              maskPlaceholder=""
              inputProps={{
                name: name,
                id: id,
                minLength: fieldData.minLength,
              }}
            />
          ) : (
            <>
              {isMaskedInput && fieldData.maskPattern ? (
                <InputMask
                  ismaskedinput="true"
                  className="form__field"
                  name={name}
                  mask={fieldData.maskPattern}
                  maskPlaceholder=""
                  id={id}
                  onChange={(e) => {
                    if (handleInputChangeFromParent) handleInputChangeFromParent(e);
                    onInputChange(e);
                  }}
                  onFocus={(e) => onFocus(e.target)}
                  onBlur={(e) => onClickAway(e.target)}
                  value={
                    name === "phone" || name === "phone-number" || name === "cell-number"
                      ? formattedPhoneNumber
                      : value || fieldData.value
                  }
                  title={fieldData.title}
                  aria-placeholder={ariaPlaceholder || fieldData.ariaPlaceholder}
                  placeholder={placeholder || fieldData.placeholder}
                  minLength={fieldData.minLength}
                  ref={inputRef}
                />
              ) : (
                <>
                  <input
                    className={`form__field${
                      type === "password" ? " form__field--with-right-button" : ""
                    } ${displayCurrencySymbol ? "currencySymbolPadding" : ""}`}
                    max={maxValue ? maxValue : null}
                    type={type === "password" ? passwordType : type}
                    value={
                      name === "phone" || name === "phone-number" || name === "cell-number"
                        ? formattedPhoneNumber
                        : value || fieldData.value
                    }
                    onKeyDown={(e) =>
                      name === "eGiftCardAmount" ? checkForPositiveInteger(e) : null
                    }
                    placeholder={placeholder || fieldData.placeholder}
                    id={id}
                    name={name}
                    onChange={(e) => {
                      if (handleInputChangeFromParent) handleInputChangeFromParent(e);
                      onInputChange(e);

                      if (name === "phone" || name === "phone-number" || name === "cell-number")
                        onTelInputChange(e);
                    }}
                    onFocus={(e) => onFocus(e.target)}
                    onBlur={(e) => onClickAway(e.target)}
                    onWheel={(e) => e.target.blur()}
                    aria-required={isRequired}
                    title={title}
                    maxLength={`${name === "credit-card-number" ? fieldData.maxLength : maxlength}`}
                    minLength={fieldData.minLength}
                    ref={inputRef}
                    autoCapitalize={name.includes("email") ? "none" : "on"} // prevent iOS auto-capitalization
                    autoComplete={autocomplete === "off" || !autocomplete ? "new-password" : ""}
                  />
                </>
              )}
            </>
          )}

          {type === "password" && (
            <PasswordVisibilityToggle
              type={passwordType}
              customAriaLabel={label}
              toggleVisibility={(newType) => setPasswordType(newType)}
            />
          )}

          {!fieldData.isValid && !showWarningMessage && (
            <div className="form__field-error">{fieldData.errorMessage}</div>
          )}
          {fieldData.isPatternTooltip && !showWarningMessage && (
            <div className="form__field-error">
              {appLabels["form"]["enter-in-input-mask-pattern-format"].split("[mask-pattern]")[0] +
                appLabels["form"]["enter-in-input-mask-pattern-format"].split("[mask-pattern]")[1]}
            </div>
          )}
          {!!phoneNumberErrorMessage && (
            <div className="form__field-error">{phoneNumberErrorMessage}</div>
          )}
          {showWarningMessage && (
            <div className="form__field-error">
              {appLabels["form"]["do-not-include-country-code"]}
            </div>
          )}
        </div>
      )}
    </>
  );
};

FormInput.displayName = "FormInput";
